/**
 * @file src/components/Spinner/WholePageLoader.tsx
 * @exports WholePageLoader
 * @summary Full-page loading overlay with an accessible live region so screen readers announce the loading state.
 */

// NOTE: Do not use styled-components in this file - it will cause a hydration error
import { useEffect, useState } from "react";

import { Spinner } from "./Spinner";

export const LOADING_TEXT = "Loading...";

interface WholePageLoaderProps {
	/** Text announced by screen readers. Defaults to "Loading…". */
	statusText?: string;
}

export const WholePageLoader = ({ statusText = LOADING_TEXT }: WholePageLoaderProps) => {
	// Start empty so the live region is registered before text appears.
	// Screen readers only announce *changes* inside a live region — if the text
	// is present when the region first mounts, VoiceOver never fires.
	const [announcedText, setAnnouncedText] = useState("");

	useEffect(() => {
		const id = setTimeout(() => setAnnouncedText(statusText), 10);
		return () => clearTimeout(id);
	}, [statusText]);

	return (
		<div
			id="whole-page-loader"
			role="status"
			aria-live="polite"
			className="fixed inset-0 z-[1000] flex items-center justify-center bg-white"
		>
			{/* aria-hidden prevents the SVG <title> from being announced inside the live region */}
			<div aria-hidden="true" className="flex flex-col h-[48px] items-center justify-center">
				<Spinner />
			</div>
			<p className="sr-only">{announcedText}</p>
		</div>
	);
};
